import React from 'react';
import { useLocation } from 'react-router-dom';

import { Modal as AntdModal } from 'antd';
import { filterUndefinedOrNull } from 'utils';

import {
  ComputedResponseDtoListActionsEnum,
  ComputedResponseDtoListAvailableFilesEnum,
} from 'lib_api/lib/api/gen';

import ActionAndFileWrapper from 'components/ActionAndFileWrapper/ActionAndFileWrapper';
import { AvailableAction } from 'components/ActionAndFileWrapper/types';
import ImporterPieceJointe from 'components/ImporterPieceJointe/ImporterPieceJointe';
import BaseModal from 'components/WrappedComponents/BaseModal';
import { useDossierContext } from 'hooks/dossiers/DossierContext';
import { useGFFilterContext } from 'hooks/GFFilterStoreContext';
import { useModal } from 'hooks/modal/useModal';
import { useDisplayActionsRequisesBanner } from 'hooks/useActionsRequises';
import { ActionsRequisesLinkState } from 'utils/actionsRequisesUtils';
import { BaseModalWidthUtils } from 'utils/BaseModalWidthUtils';
import { extractNonnullDossierIdOrThrow } from 'utils/dossierUtils';

import BonEnlevementGardienForm from '../../Components/SubForms/BonEnlevementGardienSubForm/BonEnlevementGardienForm';
import EntreeVehiculeForm from '../../Components/SubForms/EntreeVehiculeSubForm/EntreeVehiculeForm';
import SortieVehiculeForm from '../../Components/SubForms/SortieVehiculeSubForm/SortieVehiculeForm';

const ActionsSuiviVehicule = (): React.ReactElement => {
  const { state } = useLocation() as { state: ActionsRequisesLinkState };
  const [isSortieOpen, setIsSortieOpen] = React.useState<boolean>(false);
  const [isBonEnlevementOpen, setIsBonEnlevementOpen] = React.useState<boolean>(
    state?.openSaisieVhu || false,
  );
  const [isEnregistrerEntreeOpen, setIsEnregistrerEntreeOpen] =
    React.useState<boolean>(state?.openEntreeVehicule || false);

  const [{ result: dossier, setDossier }] = useDossierContext();
  const [, , gfActionsRequises] = useGFFilterContext();
  const dossierId = extractNonnullDossierIdOrThrow(dossier);

  const importerPieceJointeModal = useModal();

  // display the actions requises banner
  useDisplayActionsRequisesBanner(dossier, gfActionsRequises);

  function mapComputedListActionsEnumToAvailableAction(
    actionEnum: ComputedResponseDtoListActionsEnum,
  ): AvailableAction | null {
    switch (actionEnum) {
      case ComputedResponseDtoListActionsEnum.SAISIE_VHU:
        return {
          title: 'Renseigner le centre VHU',
          onClick: () => {
            setIsBonEnlevementOpen(true);
          },
        };

      case ComputedResponseDtoListActionsEnum.RENSEIGNER_SORTIE_VEHICULE:
        return {
          title: 'Enregistrer la sortie',
          onClick: () => {
            setIsSortieOpen(true);
          },
        };

      case ComputedResponseDtoListActionsEnum.ENREGISTRER_ENTREE:
        return {
          title: "Enregistrer l'entrée",
          onClick: () => {
            setIsEnregistrerEntreeOpen(true);
          },
        };
      case ComputedResponseDtoListActionsEnum.IMPORTER_PIECES_JOINTES:
        return {
          title: 'Joindre un document',
          onClick: importerPieceJointeModal.open,
        };
      default:
        return null;
    }
  }

  const filesList: ComputedResponseDtoListAvailableFilesEnum[] =
    dossier.computed?.listAvailableFiles ?? [];
  const actions: AvailableAction[] = filterUndefinedOrNull(
    dossier.computed?.listActions?.map(action => {
      return mapComputedListActionsEnumToAvailableAction(action);
    }) || [],
  );

  return (
    <>
      <ActionAndFileWrapper
        filesList={filesList}
        piecesJointesList={dossier.computed?.listAvailablePiecesJointes ?? []}
        actions={actions}
        dossierId={dossierId}
      />

      {/* Action sortie du véhicule */}
      <AntdModal
        visible={isSortieOpen}
        destroyOnClose={true}
        footer={null}
        onCancel={() => setIsSortieOpen(false)}
        width={BaseModalWidthUtils.halfPercentWidth}
      >
        <SortieVehiculeForm dossier={dossier} />
      </AntdModal>

      {/* Action entrée véhicule */}
      <AntdModal
        visible={isEnregistrerEntreeOpen}
        destroyOnClose={true}
        footer={null}
        onCancel={() => setIsEnregistrerEntreeOpen(false)}
      >
        <EntreeVehiculeForm close={() => setIsEnregistrerEntreeOpen(false)} />
      </AntdModal>

      {/* Action renseignement des coordonnées VHU */}
      <AntdModal
        visible={isBonEnlevementOpen}
        destroyOnClose={true}
        footer={null}
        onCancel={() => setIsBonEnlevementOpen(false)}
        width={'40%'}
      >
        <BonEnlevementGardienForm
          dossier={dossier}
          setDossier={setDossier}
          close={() => setIsBonEnlevementOpen(false)}
        />
      </AntdModal>

      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={importerPieceJointeModal.visible}
        closeModal={importerPieceJointeModal.close}
      >
        <ImporterPieceJointe closeModal={importerPieceJointeModal.close} />
      </BaseModal>
    </>
  );
};

export default ActionsSuiviVehicule;
